import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, AlertCircle, FileText, ArrowRight, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { useAuth } from "../context/AuthContext";
import { getEmployeeById, getLeavesByEmployee, isWFH } from "../services/leaveApiService";
import api from "../api";
import "../styles/applyLeavePage.css";

// ─── helpers ─────────────────────────────────────────────────
function formatDuration(days) {
  return `${days} day${days !== 1 ? "s" : ""}`;
}

export default function ApplyWFHForm() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const employeeId = user?.employee_id ?? user?.employeeId ?? user?.empId ?? user?.id ?? null;
  const [managerName, setManagerName] = useState("Your Manager");
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [wfhLeaveTypeId, setWfhLeaveTypeId] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!employeeId || !token) return;

    // Load manager name & leave history to check WFH limit, and fetch leave types
    Promise.all([
      getEmployeeById(employeeId, token).catch(() => null),
      getLeavesByEmployee(employeeId, token).catch(() => []),
      api.get("/api/leave-types", { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
    ]).then(([emp, history, typesRes]) => {
      if (emp) setManagerName(emp.manager_name ?? "Your Manager");
      setLeaveHistory(history);

      const types = typesRes.data || [];
      const wfhType = types.find(t => t.code === 'WFH' || (t.name || '').toUpperCase().includes('WFH'));
      if (wfhType) setWfhLeaveTypeId(wfhType.id);
    }).finally(() => {
      setLoadingData(false);
    });
  }, [employeeId, token]);

  const minDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2); // allow slightly backdated
    return d.toISOString().split("T")[0];
  }, []);

  const requestedUnits = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.max(0, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1);
  }, [startDate, endDate]);

  const wfhUsedThisMonth = useMemo(() => {
    if (!startDate) return 0;
    const selectedDate = new Date(startDate);
    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    return leaveHistory
      .filter((h) =>
        isWFH(h) &&
        (h.status === 'APPROVED' || h.status === 'PENDING') &&
        new Date(h.start_date).getMonth() === selectedMonth &&
        new Date(h.start_date).getFullYear() === selectedYear
      )
      .reduce((sum, h) => sum + parseFloat(h.total_units || 0), 0);
  }, [startDate, leaveHistory]);

  const isExceeded = requestedUnits + wfhUsedThisMonth > 1;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!startDate || !endDate || !reason) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.error("End date cannot be before start date.");
      return;
    }

    if (isExceeded) {
      toast.error("Work from home is limited to 1 day per month. Please submit an Exception Request instead.");
      return;
    }

    if (!wfhLeaveTypeId) {
      toast.error("Work From Home option is not configured in the system.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        employee_id: employeeId,
        leave_type_id: wfhLeaveTypeId,
        start_date: startDate,
        end_date: endDate,
        total_units: requestedUnits,
        request_type: "FULL_DAY",
        reason: reason
      };

      await api.post(`/api/leave-requests`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("Work From Home request submitted successfully!");
      setStartDate("");
      setEndDate("");
      setReason("");

      // Refresh history
      getLeavesByEmployee(employeeId, token).then(setLeaveHistory).catch(() => { });

    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to submit WFH request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingData) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>;
  }

  return (
    <div className="al-embedded-container" style={{ width: '100%', maxWidth: '800px', margin: '0 auto', background: '#fff', borderRadius: '12px', padding: '24px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}>Work From Home</h2>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Apply for your monthly allowed Work From Home day.</p>

      {/* Inline exceeded warning removed per user request */}

      <div className="al-date-row">
        <div className="al-field">
          <label className="al-label">Start Date <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            type="date"
            className="al-input"
            value={startDate}
            min={minDateStr}
            onClick={(e) => e.target.showPicker()}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="al-field">
          <label className="al-label">End Date <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            type="date"
            className="al-input"
            value={endDate}
            min={startDate || minDateStr}
            onClick={(e) => e.target.showPicker()}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <div className="al-field" style={{ marginTop: '20px' }}>
        <label className="al-label">Reason <span style={{ color: '#ef4444' }}>*</span></label>
        <textarea
          className="al-input"
          rows={3}
          placeholder="Briefly explain your reason for working from home..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      {requestedUnits > 0 && !isExceeded && (
        <div className="al-time-summary" style={{ marginTop: '16px' }}>
          <span className="al-time-summary-icon">⏱</span>
          <span>Duration: <strong>{formatDuration(requestedUnits)}</strong></span>
        </div>
      )}

      <div className="al-request-sent-to" style={{ marginTop: '24px' }}>
        <User size={18} />
        <span>Request will be sent to <strong>{managerName}</strong></span>
      </div>

      <div className="al-form-actions">
        <button
          type="button"
          className={`al-submit-btn${isExceeded ? " disabled" : ""}`}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <><Loader2 size={14} className="al-spin" /> Submitting...</>
          ) : (
            "Submit WFH Request"
          )}
        </button>
        <button type="button" className="al-cancel-btn" onClick={() => { setStartDate(""); setEndDate(""); setReason(""); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
